import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  useTheme,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Stack,
  TextField,
  MenuItem,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";

const ExpensesSettings = () => {
  const theme = useTheme();
  const { setOverlayData, setOverlayName, showSnackbar } = useStateContext();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    detailId: null,
    subCategoryId: null,
  });
  const [detailForm, setDetailForm] = useState({
    Name: "",
    Description: "",
    SortOrder: 0,
    IsActive: true,
  });

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get("/expenses/categories");
      // Ensure categories have subcategories loaded
      const categoriesWithSubs = await Promise.all(
        response.data.map(async (category) => {
          const subsResponse = await api.get(
            `/expenses/subcategories?categoryId=${category.id}`,
          );

          const subCategoriesWithDetails = await Promise.all(
            (subsResponse.data || []).map(async (subCategory) => {
              const detailsResponse = await api.get(
                `/expenses/details?subCategoryId=${subCategory.id}&includeInactive=true`,
              );

              return {
                ...subCategory,
                Details: detailsResponse.data || [],
              };
            }),
          );

          return {
            ...category,
            SubCategories: subCategoriesWithDetails,
          };
        }),
      );
      setCategories(categoriesWithSubs);
    } catch (error) {
      console.error("Error fetching categories:", error);
      showSnackbar("Error loading expense categories", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  // Fetch categories and subcategories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleAddCategory = () => {
    setOverlayData({
      categoryId: null,
      categoryName: "",
      categoryDescription: "",
      categoryIRSCode: "",
      subcategories: [],
      onSaved: fetchCategories,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_EXPENSES_CATEGORY_NEW_EDIT);
  };

  const handleEditCategory = (category) => {
    setOverlayData({
      categoryId: category.id,
      categoryName: category.Name,
      categoryDescription: category.Description || "",
      categoryIRSCode: category.IRSCode || "",
      subcategories: category.SubCategories || [],
      onSaved: fetchCategories,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_EXPENSES_CATEGORY_NEW_EDIT);
  };

  const handleDeleteCategory = async () => {
    if (!deleteConfirm || !deleteConfirm.id) return;

    try {
      await api.delete(`/expenses/categories/${deleteConfirm.id}`);
      showSnackbar("Category deleted successfully", "success");
      setDeleteConfirm(null);
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      showSnackbar("Error deleting category", "error");
    }
  };

  const handleAddSubcategory = (category) => {
    setOverlayData({
      subcategoryId: null,
      subcategoryName: "",
      subcategoryDescription: "",
      categoryId: category.id,
      onSaved: fetchCategories,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_EXPENSES_SUBCATEGORY_NEW_EDIT);
  };

  const handleEditSubcategory = (subcategory, categoryId) => {
    setOverlayData({
      subcategoryId: subcategory.id,
      subcategoryName: subcategory.Name,
      subcategoryDescription: subcategory.Description || "",
      categoryId,
      onSaved: fetchCategories,
    });
    setOverlayName(OVERLAY_NAMES.SETTINGS_EXPENSES_SUBCATEGORY_NEW_EDIT);
  };

  const handleDeleteSubcategory = async (subcategoryId) => {
    try {
      await api.delete(`/expenses/subcategories/${subcategoryId}`);
      showSnackbar("Subcategory deleted successfully", "success");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting subcategory:", error);
      showSnackbar("Error deleting subcategory", "error");
    }
  };

  const openEditDetailDialog = (detail) => {
    setDetailDialog({
      open: true,
      detailId: detail.id,
      subCategoryId: detail.SubCategoryID,
    });
    setDetailForm({
      Name: detail.Name || "",
      Description: detail.Description || "",
      SortOrder: Number(detail.SortOrder) || 0,
      IsActive: detail.IsActive !== false,
    });
  };

  const closeDetailDialog = () => {
    setDetailDialog({
      open: false,
      detailId: null,
      subCategoryId: null,
    });
    setDetailForm({
      Name: "",
      Description: "",
      SortOrder: 0,
      IsActive: true,
    });
  };

  const handleSaveDetail = async () => {
    try {
      if (!detailDialog.detailId) {
        showSnackbar("Only existing expense details can be edited here", "warning");
        return;
      }

      const payload = {
        SubCategoryID: detailDialog.subCategoryId,
        Name: detailForm.Name?.trim(),
        Description: detailForm.Description?.trim() || null,
        SortOrder: Number(detailForm.SortOrder) || 0,
        IsActive: detailForm.IsActive,
      };

      await api.put(`/expenses/details/${detailDialog.detailId}`, payload);
      showSnackbar("Expense detail updated", "success");

      closeDetailDialog();
      fetchCategories();
    } catch (error) {
      console.error("Error saving expense detail:", error);
      showSnackbar(
        error?.response?.data?.error || "Error saving expense detail",
        "error",
      );
    }
  };

  const handleDeleteDetail = async (detailId) => {
    try {
      await api.delete(`/expenses/details/${detailId}`);
      showSnackbar("Expense detail deleted", "success");
      fetchCategories();
    } catch (error) {
      console.error("Error deleting expense detail:", error);
      showSnackbar(
        error?.response?.data?.error || "Error deleting expense detail",
        "error",
      );
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Fixed Header Section */}
      <Box sx={{ flexShrink: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h3" fontWeight="bold">
            Expense Categories
          </Typography>
          <IconButton
            aria-label="Add expense category"
            onClick={handleAddCategory}
            sx={(theme) => ({
              height: 36,
              width: 36,
              p: 0,
              borderRadius: "12px",
              backgroundColor: theme.palette.background.default,
              border: "1px solid",
              borderColor: theme.palette.divider,
              color: theme.palette.primary.main,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              "&:hover": {
                borderColor: theme.palette.primary.main,
              },
              "&:focus-visible": {
                borderColor: theme.palette.primary.main,
                outline: "none",
              },
            })}
          >
            <AddRoundedIcon />
          </IconButton>
        </Box>

        <Divider sx={{ my: 2 }} />
      </Box>

      {/* Scrollable Content Section */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : categories.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "90%",
              p: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No categories yet. Click the + button to add one.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {categories.map((category) => (
              <Grid key={category.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  sx={{
                    bgcolor: theme.palette.background.default,
                    border: `1px solid ${theme.palette.primary.border2}`,
                    borderRadius: "12px",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: theme.shadows[8],
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      pb: 1.5,
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Category Header */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h5"
                          fontWeight="bold"
                          sx={{ color: theme.palette.primary.main }}
                        >
                          {category.Name}
                        </Typography>
                        {category.IRSCode && (
                          <Typography variant="caption" color="text.secondary">
                            IRS Code: {category.IRSCode}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleEditCategory(category)}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": { bgcolor: theme.palette.action.hover },
                          }}
                        >
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirm(category)}
                          sx={{
                            color: theme.palette.error.main,
                            "&:hover": { bgcolor: theme.palette.action.hover },
                          }}
                        >
                          <DeleteRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Description */}
                    {category.Description && (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {category.Description}
                        </Typography>
                      </>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Subcategories */}
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ color: theme.palette.primary.text }}
                        >
                          Subcategories ({category.SubCategories?.length || 0})
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleAddSubcategory(category)}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": { bgcolor: theme.palette.action.hover },
                          }}
                        >
                          <AddRoundedIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      {category.SubCategories &&
                      category.SubCategories.length > 0 ? (
                        <Box
                          sx={{
                            maxHeight: 180,
                            overflowY: "auto",
                            mb: 2,
                          }}
                        >
                          <Stack spacing={1}>
                            {category.SubCategories.map((sub) => (
                              <Box
                                key={sub.id}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  p: 1,
                                  bgcolor: theme.palette.background.alt,
                                  borderRadius: "8px",
                                  border: `1px solid ${theme.palette.divider}`,
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography
                                    variant="body2"
                                    fontWeight="500"
                                    noWrap
                                  >
                                    {sub.Name}
                                  </Typography>
                                  {sub.Description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        display: "-webkit-box",
                                        overflow: "hidden",
                                        WebkitBoxOrient: "vertical",
                                        WebkitLineClamp: 1,
                                      }}
                                    >
                                      {sub.Description}
                                    </Typography>
                                  )}

                                  <Box sx={{ mt: 0.75 }}>
                                    <Box sx={{ mb: 0.5 }}>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Details ({sub.Details?.length || 0})
                                      </Typography>
                                    </Box>

                                    {sub.Details && sub.Details.length > 0 ? (
                                      <Stack spacing={0.5}>
                                        {sub.Details.map((detail) => (
                                          <Box
                                            key={detail.id}
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "space-between",
                                              gap: 1,
                                              p: "2px 6px",
                                              borderRadius: "6px",
                                              bgcolor:
                                                theme.palette.background
                                                  .default,
                                              border: `1px solid ${theme.palette.divider}`,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                minWidth: 0,
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontWeight: 500,
                                                  overflow: "hidden",
                                                  textOverflow: "ellipsis",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                {detail.Name}
                                              </Typography>
                                              {detail.IsActive === false && (
                                                <Chip
                                                  size="small"
                                                  label="Inactive"
                                                  sx={{ height: 18 }}
                                                />
                                              )}
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                              >
                                                #{detail.SortOrder || 0}
                                              </Typography>
                                            </Box>
                                            <Box
                                              sx={{
                                                display: "flex",
                                                gap: 0.25,
                                              }}
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  openEditDetailDialog(detail)
                                                }
                                                sx={{
                                                  color:
                                                    theme.palette.primary.main,
                                                  padding: "2px",
                                                }}
                                              >
                                                <EditRoundedIcon fontSize="inherit" />
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleDeleteDetail(detail.id)
                                                }
                                                sx={{
                                                  color:
                                                    theme.palette.error.main,
                                                  padding: "2px",
                                                }}
                                              >
                                                <DeleteRoundedIcon fontSize="inherit" />
                                              </IconButton>
                                            </Box>
                                          </Box>
                                        ))}
                                      </Stack>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        No details yet
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.5,
                                    ml: 1,
                                  }}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleEditSubcategory(sub, category.id)
                                    }
                                    sx={{
                                      color: theme.palette.primary.main,
                                      padding: "4px",
                                      "&:hover": {
                                        bgcolor: theme.palette.action.hover,
                                      },
                                    }}
                                  >
                                    <EditRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      handleDeleteSubcategory(sub.id)
                                    }
                                    sx={{
                                      color: theme.palette.error.main,
                                      padding: "4px",
                                      "&:hover": {
                                        bgcolor: theme.palette.action.hover,
                                      },
                                    }}
                                  >
                                    <DeleteRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      ) : (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          No subcategories yet
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Delete Category Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirm && deleteConfirm.type !== "subcategory"}
        onClose={() => setDeleteConfirm(null)}
      >
        <DialogTitle>Delete Category?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.Name}</strong>? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button
            onClick={handleDeleteCategory}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={detailDialog.open}
        onClose={closeDetailDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Edit Expense Detail
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              size="small"
              value={detailForm.Name}
              onChange={(event) =>
                setDetailForm((previous) => ({
                  ...previous,
                  Name: event.target.value,
                }))
              }
              required
              fullWidth
            />
            <TextField
              label="Description"
              size="small"
              multiline
              minRows={2}
              value={detailForm.Description}
              onChange={(event) =>
                setDetailForm((previous) => ({
                  ...previous,
                  Description: event.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Sort Order"
              size="small"
              type="number"
              value={detailForm.SortOrder}
              onChange={(event) =>
                setDetailForm((previous) => ({
                  ...previous,
                  SortOrder: event.target.value,
                }))
              }
              fullWidth
            />
            <TextField
              label="Status"
              size="small"
              select
              value={detailForm.IsActive ? "active" : "inactive"}
              onChange={(event) =>
                setDetailForm((previous) => ({
                  ...previous,
                  IsActive: event.target.value === "active",
                }))
              }
              fullWidth
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetailDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDetail}
            disabled={!detailForm.Name?.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpensesSettings;
